import { Dispatch } from "redux";
import {
  setProofDoc,
  setSalaryProofDoc,
  setBankDoc,
} from "../features/merchant/doc";
import { hydrateProfState } from "../features/indinvestor/profSlice";
import { hydrateBankState } from "../features/indinvestor/bankSliceInd";

/**
 * Individual investor draft data structure
 */
interface IndividualDraftData {
  // Documents
  proofOfAddress?: any[];
  salaryProof?: any[];
  bankStatement?: any[];

  // Industry
  industryGroup?: string;
  industry?: string;
  industryOthers?: string;
  industryDropdownValues?: any;

  // Personal
  numberOfTaxResidenceCountries?: number;
  taxResidenceCountries?: string[];
  phoneNumber?: string;
  numberofYears?: number;
  experience?: string;
  occupation?: string;
  nationality?: string;
  gender?: string;
  employmentStatus?: string;
  employmentPosition?: string;
  dateOfBirth?: string;
  countryOfBirth?: string;
  countryOfResidence?: string;
  isPep?: boolean;
  employerName?: string;
  referralSource?: string;
  otherReferral?: string;

  // Address
  legalEntityAddress?: string;
  postalCode?: string;
  investmentAmount?: number;

  // Bank
  bankName?: string;
  accountType?: string;
  accountNumber?: string;
  accountHolderName?: string;
  swiftCode?: string;
}

/**
 * Initialize document uploads using unwrapped payloads
 */
export const initializeDocuments = (
  dispatch: Dispatch,
  data: IndividualDraftData
) => {
  dispatch(setProofDoc(data.proofOfAddress || []));
  dispatch(setSalaryProofDoc(data.salaryProof || []));
  dispatch(setBankDoc(data.bankStatement || []));
};

/**
 * Initialize industry details using hydrate pattern
 */
export const initializeIndustryDetails = (
  dispatch: Dispatch,
  data: IndividualDraftData
) => {
  dispatch(
    hydrateProfState({
      industryGroup: data.industryGroup ?? null,
      industry: data.industry ?? null,
      industryOthers: data.industryOthers ?? null,
      industryDropdownValues: data.industryDropdownValues ?? null,
    })
  );
};

/**
 * Initialize personal details using hydrate pattern
 */
export const initializePersonalDetails = (
  dispatch: Dispatch,
  data: IndividualDraftData
) => {
  const experienceValue = data.numberofYears?.toString() || data.experience;

  dispatch(
    hydrateProfState({
      numberOfTaxResidenceCountries: data.numberOfTaxResidenceCountries ?? null,
      taxResidenceCountries: data.taxResidenceCountries || [],
      phoneNumber: data.phoneNumber ?? null,
      industryGroup: data.industryGroup ?? null,
      numberofYears: data.numberofYears ?? null,
      occupation: data.occupation ?? null,
      experience: experienceValue ?? null,
      nationality: data.nationality ?? null,
      gender: data.gender ?? null,
      employmentStatus: data.employmentStatus ?? null,
      employmentPosition: data.employmentPosition ?? null,
      dateOfBirth: data.dateOfBirth ?? null,
      countryOfBirth: data.countryOfBirth ?? null,
      countryOfResidence: data.countryOfResidence ?? null,
      isPep: data.isPep ?? null,
      employerName: data.employerName ?? null,
      referralSource: data.referralSource ?? null,
      otherReferral: data.otherReferral ?? null,
    })
  );
};

/**
 * Initialize address details using hydrate pattern
 */
export const initializeAddressDetails = (
  dispatch: Dispatch,
  data: IndividualDraftData
) => {
  dispatch(
    hydrateProfState({
      countryOfResidence: data.countryOfResidence ?? null,
      address: data.legalEntityAddress ?? null,
      postalCode: data.postalCode ?? null,
      phoneNumber: data.phoneNumber ?? null,
      investmentAmount: Number(data.investmentAmount) || 0,
    })
  );
};

/**
 * Initialize bank details using hydrate pattern
 */
export const initializeBankDetails = (
  dispatch: Dispatch,
  data: IndividualDraftData
) => {
  dispatch(
    hydrateBankState({
      bankName: data.bankName ?? null,
      accountType: data.accountType ?? null,
      accountNumber: data.accountNumber ?? null,
      accountHolderName: data.accountHolderName ?? null,
      swiftCode: data.swiftCode ?? null,
    })
  );
};
